import posthog from 'posthog-js';

import useReplyToClientActionsStore from '@/features/compose/stores/useReplyToClientActionsStore';

import getAllEmailAddressesFromThread from '../outlook/getAllEmailAddressesFromThread';
import getBody from '../outlook/getBody';
import getEmlBlob from '../outlook/getEmlBlob';
import isComposeMode from '../outlook/isComposeMode';

export default function useOutlookClient() {
  const lastMessageId = useReplyToClientActionsStore(
    (state) => state.lastMessageId,
  );

  const getSelectedMessageId = () => {
    const item = Office.context.mailbox?.item;
    if (item?.internetMessageId) return item.internetMessageId;

    const inReplyTo = item?.inReplyTo;
    if (inReplyTo && inReplyTo !== '') return inReplyTo;

    return lastMessageId;
  };

  const getSelectedMessageSender = () => {
    const from = Office.context.mailbox?.item?.from;
    if (!from) return undefined;

    return { name: from.displayName, address: from.emailAddress };
  };

  const getSelectedMessageDetails = async () => {
    const item = Office.context.mailbox.item;
    if (!item) return undefined;

    const messageId = item.internetMessageId;

    const fromAddress = item.from.emailAddress;
    const subject = item.subject;
    const threadEmailAddresses = await getAllEmailAddressesFromThread();

    return { fromAddress, subject, threadEmailAddresses, messageId };
  };

  const getClientInfo = async () => {
    const { host, platform } = await Office.onReady();

    const { hostName, OWAView, hostVersion } =
      Office.context.mailbox.diagnostics;

    return {
      client: 'outlook',
      host,
      platform,
      diagnostics: { hostName, OWAView, hostVersion },
    };
  };

  const openSendMail = ({
    to,
    cc,
    bcc,
    subject,
    body,
  }: {
    to?: string[];
    cc?: string[];
    bcc?: string[];
    subject?: string;
    body?: string;
  }) => {
    const mailbox = Office?.context?.mailbox;

    if (typeof mailbox?.displayNewMessageForm !== 'function')
      throw new Error('displayNewMessageForm is not available');

    posthog.capture('Open email client\'s "send email"', {
      client: 'outlook',
      to,
      cc,
      bcc,
      subject,
      body,
    });

    mailbox.displayNewMessageForm({
      toRecipients: to ?? [],
      ccRecipients: cc ?? [],
      bccRecipients: bcc ?? [],
      subject: subject ?? '',
      htmlBody: body ?? '',
    });
  };

  const getEmailBody = (
    format: 'Html' | 'Text' = 'Text',
    options?: { hostConfig?: boolean },
  ) => getBody(format, options);

  const mode = isComposeMode() ? ('Compose' as const) : ('Read' as const);

  return {
    mode,
    getSelectedMessageId,
    getSelectedMessageSender,
    getAllEmailAddressesFromThread,
    getSelectedMessageDetails,
    openSendMail,
    getClientInfo,
    getEmailBody,
    getEmlBlob,
  };
}
